import { ComponentProps } from 'react';

import { cn } from '@/lib/utils';

const Table = (props: ComponentProps<'table'>) => {
  const { className, ...restProps } = props;

  return (
    <div
      data-slot="table-container"
      className="border-border bg-card relative w-full overflow-x-auto rounded-lg border shadow-xs"
    >
      <table
        data-slot="table"
        className={cn('w-full caption-bottom text-sm', className)}
        {...restProps}
      />
    </div>
  );
};

const TableHeader = (props: ComponentProps<'thead'>) => {
  const { className, ...restProps } = props;

  return (
    <thead
      data-slot="table-header"
      className={cn('[&_tr]:border-b', className)}
      {...restProps}
    />
  );
};

const TableBody = (props: ComponentProps<'tbody'>) => {
  const { className, ...restProps } = props;

  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...restProps}
    />
  );
};

const TableFooter = (props: ComponentProps<'tfoot'>) => {
  const { className, ...restProps } = props;

  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        'bg-muted/50 border-t font-medium [&>tr]:last:border-b-0',
        className,
      )}
      {...restProps}
    />
  );
};

const TableRow = (props: ComponentProps<'tr'>) => {
  const { className, ...restProps } = props;

  return (
    <tr
      data-slot="table-row"
      className={cn(
        'hover:bg-table-row-hover data-[state=selected]:bg-table-row-selected data-[state=selected]:shadow-table-row-mark border-table-border focus-visible:outline-primary border-b transition-colors focus-visible:outline-1 focus-visible:-outline-offset-1 aria-disabled:pointer-events-none aria-disabled:opacity-45',
        className,
      )}
      {...restProps}
    />
  );
};

const TableHead = (props: ComponentProps<'th'>) => {
  const { className, ...restProps } = props;

  return (
    <th
      data-slot="table-head"
      className={cn(
        'text-subtle-foreground bg-table-header border-table-border text-overline h-10 px-3 py-2 text-left align-middle font-medium whitespace-nowrap uppercase not-last:border-r has-[[role=checkbox]]:pr-0 has-[button:focus-visible]:shadow-[inset_0_-2px_0_var(--color-primary)] *:[[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      {...restProps}
    />
  );
};

const TableCell = (props: ComponentProps<'td'>) => {
  const { className, ...restProps } = props;

  return (
    <td
      data-slot="table-cell"
      className={cn(
        'h-10 px-3 py-1.5 align-middle text-xs/5 wrap-break-word has-[[role=checkbox]]:pr-0 *:[[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      {...restProps}
    />
  );
};

const TableCaption = (props: ComponentProps<'caption'>) => {
  const { className, ...restProps } = props;

  return (
    <caption
      data-slot="table-caption"
      className={cn('text-muted-foreground mt-4 text-sm', className)}
      {...restProps}
    />
  );
};

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
